import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon, UploadIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { DatePicker } from '../../components/ui/DatePicker';
import { Modal } from '../../components/ui/Modal';
interface BillPaymentFormData {
  billType: 'Internal' | 'External';
  voucherDate: string;
  paymentMethod: 'Cash' | 'Bank';
  expenseHeadId?: string;
  vendorId?: string;
  vendorLedgerId?: string;
  bankNameId?: string;
  bankAccountId?: string;
  chartOfAccountId?: string;
  amount: number;
  description: string;
  notes: string;
  paidTo: string;
  attachment?: File;
}
export function BillPaymentCreate() {
  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [formData, setFormData] = useState<BillPaymentFormData>({
    billType: 'Internal',
    voucherDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'Cash',
    amount: 0,
    description: '',
    notes: '',
    paidTo: ''
  });
  const billTypes = [{
    value: 'Internal',
    label: 'Internal (Expense Head)'
  }, {
    value: 'External',
    label: 'External (Vendor)'
  }];
  const paymentMethods = [{
    value: 'Cash',
    label: 'Cash'
  }, {
    value: 'Bank',
    label: 'Bank'
  }];
  const mockExpenseHeads = [{
    value: 'exp1',
    label: 'Office Supplies Expense'
  }, {
    value: 'exp2',
    label: 'Utilities Expense'
  }, {
    value: 'exp3',
    label: 'Rent Expense'
  }, {
    value: 'exp4',
    label: 'Salaries & Wages'
  }];
  const mockVendors = [{
    value: 'vendor1',
    label: 'ABC Suppliers'
  }, {
    value: 'vendor2',
    label: 'XYZ Services Ltd.'
  }, {
    value: 'vendor3',
    label: 'DEF Trading Co.'
  }];
  const mockVendorLedgers: Record<string, Array<{
    value: string;
    label: string;
  }>> = {
    vendor1: [{
      value: 'ledger1',
      label: '2100 - ABC Suppliers Payable'
    }, {
      value: 'ledger2',
      label: '2101 - ABC Suppliers - Credit Account'
    }],
    vendor2: [{
      value: 'ledger3',
      label: '2110 - XYZ Services Payable'
    }, {
      value: 'ledger4',
      label: '2111 - XYZ Services - Trade Payable'
    }],
    vendor3: [{
      value: 'ledger5',
      label: '2120 - DEF Trading Payable'
    }]
  };
  const mockBankNames = [{
    value: 'bank1',
    label: 'Premier Bank'
  }, {
    value: 'bank2',
    label: 'Dahabshiil Bank'
  }, {
    value: 'bank3',
    label: 'Salaam Bank'
  }];
  const mockBankAccounts: Record<string, Array<{
    value: string;
    label: string;
    coaId: string;
    coaName: string;
  }>> = {
    bank1: [{
      value: 'acc1',
      label: 'Hargeisa Branch - 1234567890',
      coaId: 'coa1',
      coaName: '1200 - Premier Bank Account'
    }, {
      value: 'acc2',
      label: 'Mogadishu Branch - 1234567891',
      coaId: 'coa2',
      coaName: '1201 - Premier Bank - Mogadishu'
    }],
    bank2: [{
      value: 'acc3',
      label: 'Berbera Branch - 9876543210',
      coaId: 'coa3',
      coaName: '1210 - Dahabshiil Bank Account'
    }, {
      value: 'acc4',
      label: 'Hargeisa Branch - 9876543211',
      coaId: 'coa4',
      coaName: '1211 - Dahabshiil - Hargeisa'
    }],
    bank3: [{
      value: 'acc5',
      label: 'Main Branch - 5555555555',
      coaId: 'coa5',
      coaName: '1220 - Salaam Bank Account'
    }]
  };
  const getBankAccounts = (bankNameId: string) => {
    return mockBankAccounts[bankNameId] || [];
  };
  const getChartOfAccount = (bankNameId: string, bankAccountId: string) => {
    const accounts = mockBankAccounts[bankNameId] || [];
    const account = accounts.find(acc => acc.value === bankAccountId);
    return account ? account.coaName : '';
  };
  const getVendorLedgers = (vendorId: string) => {
    return mockVendorLedgers[vendorId] || [];
  };
  const handleBankNameChange = (bankNameId: string) => {
    setFormData({
      ...formData,
      bankNameId,
      bankAccountId: '',
      chartOfAccountId: ''
    });
  };
  const handleBankAccountChange = (bankAccountId: string) => {
    const coaName = getChartOfAccount(formData.bankNameId!, bankAccountId);
    setFormData({
      ...formData,
      bankAccountId,
      chartOfAccountId: coaName
    });
  };
  const handleVendorChange = (vendorId: string) => {
    setFormData({
      ...formData,
      vendorId,
      vendorLedgerId: ''
    });
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        alert('Only PDF, JPG, and PNG files are allowed');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      setFormData({
        ...formData,
        attachment: file
      });
    }
  };
  const validateForm = (): boolean => {
    const today = new Date().toISOString().split('T')[0];
    if (formData.voucherDate > today) {
      alert('Voucher date cannot be in the future');
      return false;
    }
    if (formData.billType === 'Internal' && !formData.expenseHeadId) {
      alert('Please select an expense head for internal bill');
      return false;
    }
    if (formData.billType === 'External') {
      if (!formData.vendorId) {
        alert('Please select a vendor for external bill');
        return false;
      }
      if (!formData.vendorLedgerId) {
        alert('Please select a vendor ledger');
        return false;
      }
    }
    if (formData.paymentMethod === 'Bank') {
      if (!formData.bankNameId || !formData.bankAccountId) {
        alert('Please select Bank Name and Bank Account');
        return false;
      }
    }
    if (formData.amount <= 0) {
      alert('Amount must be greater than zero');
      return false;
    }
    if (!formData.description.trim()) {
      alert('Please enter a description');
      return false;
    }
    return true;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      setShowConfirmModal(true);
    }
  };
  const handleConfirm = async () => {
    try {
      console.log('Creating bill payment:', formData);
      // Accounting logic:
      // Internal: Expense Head Dr., Cash/Bank Cr.
      // External: Vendor Payable Dr., Cash/Bank Cr.
      navigate('/bill-payment');
    } catch (error: any) {
      alert(error.message);
    } finally {
      setShowConfirmModal(false);
    }
  };
  const isExternal = formData.billType === 'External';
  const isBank = formData.paymentMethod === 'Bank';
  return <div className="min-h-screen bg-gray-50 p-6" data-id="element-677">
      <div className="max-w-4xl mx-auto space-y-6" data-id="element-678">
        {/* Header */}
        <div className="flex items-center gap-4" data-id="element-679">
          <Button variant="ghost" onClick={() => navigate('/bill-payment')} data-id="element-680">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-681" />
          </Button>
          <div data-id="element-682">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-683">
              Create Bill Payment
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-684">
              Record internal or external bill payment
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg border p-6 space-y-6" data-id="element-685">
          {/* Bill Type & Date */}
          <div data-id="element-686">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-687">
              Payment Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-688">
              <Select label="Bill Type" required options={billTypes} value={formData.billType} onChange={e => setFormData({
              ...formData,
              billType: e.target.value as 'Internal' | 'External',
              expenseHeadId: '',
              vendorId: '',
              vendorLedgerId: ''
            })} data-id="element-689" />

              <DatePicker label="Voucher Date" required value={formData.voucherDate} onChange={date => setFormData({
              ...formData,
              voucherDate: date
            })} max={new Date().toISOString().split('T')[0]} helperText="Cannot be future date" data-id="element-690" />

              <Select label="Payment Method" required options={paymentMethods} value={formData.paymentMethod} onChange={e => setFormData({
              ...formData,
              paymentMethod: e.target.value as 'Cash' | 'Bank',
              bankNameId: '',
              bankAccountId: '',
              chartOfAccountId: ''
            })} data-id="element-691" />
            </div>
          </div>

          {/* Accounting Info */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-692">
            <h4 className="text-sm font-semibold text-blue-900 mb-2" data-id="element-693">
              Accounting Entry
            </h4>
            <p className="text-sm text-blue-800" data-id="element-694">
              {formData.billType === 'Internal' ? `• Expense Head Dr. | ${formData.paymentMethod} Cr. (Record expense payment)` : `• Vendor Payable Dr. | ${formData.paymentMethod} Cr. (Pay vendor liability)`}
            </p>
          </div>

          {/* Bank Selection - Appears FIRST (above vendor section) */}
          {isBank && <div data-id="element-695">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-696">
                Bank Selection
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-697">
                <Select label="Bank Name" required options={mockBankNames} value={formData.bankNameId} onChange={e => handleBankNameChange(e.target.value)} data-id="element-698" />

                <Select label="Bank Account" required options={getBankAccounts(formData.bankNameId || '')} value={formData.bankAccountId} onChange={e => handleBankAccountChange(e.target.value)} disabled={!formData.bankNameId} helperText="Branch Name - Account Number" data-id="element-699" />

                {formData.chartOfAccountId && <div className="md:col-span-2" data-id="element-700">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-701">
                      Associated Chart of Account
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-900" data-id="element-702">
                      {formData.chartOfAccountId}
                    </div>
                  </div>}
              </div>
            </div>}

          {/* Ledger Selection - Appears AFTER Bank Selection */}
          <div data-id="element-703">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-704">
              Ledger Selection
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-705">
              {isExternal ? <>
                  <Select label="Vendor" required options={mockVendors} value={formData.vendorId} onChange={e => handleVendorChange(e.target.value)} helperText="Select vendor" data-id="element-706" />

                  {formData.vendorId && <Select label="Vendor Ledger" required options={getVendorLedgers(formData.vendorId)} value={formData.vendorLedgerId} onChange={e => setFormData({
                ...formData,
                vendorLedgerId: e.target.value
              })} helperText="Associated vendor ledger account" data-id="element-707" />}
                </> : <Select label="Expense Head" required options={mockExpenseHeads} value={formData.expenseHeadId} onChange={e => setFormData({
              ...formData,
              expenseHeadId: e.target.value
            })} helperText="Select expense category" data-id="element-708" />}

              <Input label="Amount" type="number" required value={formData.amount} onChange={e => setFormData({
              ...formData,
              amount: Number(e.target.value)
            })} min="0" step="0.01" helperText="Payment amount" data-id="element-709" />
            </div>
          </div>

          {/* Description & Details */}
          <div data-id="element-710">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-711">
              Payment Information
            </h3>
            <div className="space-y-4" data-id="element-712">
              <Input label="Description" required value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} placeholder="Brief description of payment" data-id="element-713" />

              <Input label="Paid To" value={formData.paidTo} onChange={e => setFormData({
              ...formData,
              paidTo: e.target.value
            })} placeholder="Recipient name (optional)" helperText="Person or entity receiving payment" data-id="element-714" />

              <div data-id="element-715">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-716">
                  Notes
                </label>
                <textarea className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500" rows={3} value={formData.notes} onChange={e => setFormData({
                ...formData,
                notes: e.target.value
              })} placeholder="Additional notes or remarks..." data-id="element-717" />
              </div>

              <div data-id="element-718">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-719">
                  Attachment <span className="text-gray-500" data-id="element-720">(Optional)</span>
                </label>
                <div className="flex items-center gap-2" data-id="element-721">
                  <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50" data-id="element-722">
                    <UploadIcon className="w-4 h-4 text-gray-600" data-id="element-723" />
                    <span className="text-sm text-gray-700" data-id="element-724">Choose File</span>
                    <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} data-id="element-725" />
                  </label>
                  {formData.attachment && <span className="text-sm text-gray-600" data-id="element-726">
                      {formData.attachment.name}
                    </span>}
                </div>
                <p className="text-xs text-gray-500 mt-1" data-id="element-727">
                  PDF, JPG, PNG (Max 5MB)
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t" data-id="element-728">
            <Button type="button" variant="secondary" onClick={() => navigate('/bill-payment')} data-id="element-729">
              Cancel
            </Button>
            <Button type="submit" variant="primary" data-id="element-730">
              <SaveIcon className="w-4 h-4 mr-2" data-id="element-731" />
              Create Bill Payment
            </Button>
          </div>
        </form>
      </div>

      {/* Confirmation Modal */}
      <Modal isOpen={showConfirmModal} onClose={() => setShowConfirmModal(false)} title="Confirm Bill Payment" footer={<>
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)} data-id="element-733">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirm} data-id="element-734">
              Confirm
            </Button>
          </>} data-id="element-732">
        <div className="space-y-3" data-id="element-735">
          <p className="text-gray-700" data-id="element-736">
            Please review the bill payment details:
          </p>
          <div className="p-3 bg-gray-50 rounded text-sm space-y-1" data-id="element-737">
            <div data-id="element-738">
              <strong data-id="element-739">Type:</strong> {formData.billType}
            </div>
            <div data-id="element-740">
              <strong data-id="element-741">Date:</strong> {formData.voucherDate}
            </div>
            <div data-id="element-742">
              <strong data-id="element-743">Payment Method:</strong> {formData.paymentMethod}
            </div>
            <div data-id="element-744">
              <strong data-id="element-745">Amount:</strong> ${formData.amount.toLocaleString()}
            </div>
            <div data-id="element-746">
              <strong data-id="element-747">Description:</strong> {formData.description}
            </div>
          </div>
          <p className="text-sm text-gray-600" data-id="element-748">
            This will create a voucher and update the ledgers automatically.
          </p>
        </div>
      </Modal>
    </div>;
}